import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Switch,
    FormControlLabel,
    Button,
    Divider,
    Alert
} from '@mui/material';
import {
    Search as SearchIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import { showToast } from '@utils';
import { useSaveAiChatSettingsMutation, useGetAiChatSettingsQuery } from '../../../../../store/feature/chat/knowledgeBase';

const Config = () => {
    const { data: aiChatSettings, isLoading: isLoadingAiChatSettings, refetch } = useGetAiChatSettingsQuery();

    // Initialize with default values to avoid undefined
    const [config, setConfig] = useState({
        enable_file_search: false,
        enable_db_search: false
    });
    
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    // Update config when aiChatSettings is loaded
    useEffect(() => {
        if (aiChatSettings && !isInitialized) {
            setConfig({
                enable_file_search: aiChatSettings.enable_file_search || false,
                enable_db_search: aiChatSettings.enable_db_search || false
            });
            setIsInitialized(true);
        }
    }, [aiChatSettings, isInitialized]);

    // Handle configuration change
    const handleConfigChange = (field, value) => {
        setConfig(prev => ({
            ...prev,
            [field]: value
        }));
        setSaveSuccess(false); // Reset success message when config changes
    };


    const [saveAiChatSettings] = useSaveAiChatSettingsMutation();

    // Handle save configuration
    const handleSaveConfig =  (e) => {
        e.preventDefault();
        
        // Don't save if not initialized yet
        if (!isInitialized && !aiChatSettings) {
            showToast('warning', 'Please wait for configuration to load');
            return;
        }
        
        saveAiChatSettings(config).unwrap()
            .then(() => {
                setSaveSuccess(true);
                showToast('success', 'Configuration saved successfully');
            })
            .catch(() => {
                showToast('error', 'Failed to save configuration');
            });
    };

    // Show loading state if settings are being loaded
    if (isLoadingAiChatSettings) {
        return (
            <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Loading Configuration...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Search Configuration
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Configure which search methods are enabled for your private
                    knowledge base
                </Typography>
            </Box>

            {/* Success Alert */}
            {saveSuccess && (
                <Alert
                    severity="success"
                    sx={{ mb: 3 }}
                    onClose={() => setSaveSuccess(false)}
                >
                    Configuration saved successfully!
                </Alert>
            )}

            {/* Configuration Settings */}
            <Paper sx={{ p: 3, borderRadius: '8px' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    Search Methods
                </Typography>

                {/* File Search Configuration */}
                <Box sx={{ mb: 3 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={config.enable_file_search}
                                onChange={(e) =>
                                    handleConfigChange(
                                        'enable_file_search',
                                        e.target.checked
                                    )
                                }
                                color="primary"
                            />
                        }
                        label={
                            <Box sx={{ ml: 1 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    <SearchIcon
                                        color="primary"
                                        fontSize="small"
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        Enable File Search
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ mt: 0.5 }}
                                >
                                    Allow searching through uploaded files and
                                    documents in your knowledge base
                                </Typography>
                            </Box>
                        }
                        labelPlacement="end"
                        sx={{
                            alignItems: 'flex-start',
                            ml: 0,
                            '& .MuiFormControlLabel-label': {
                                mt: 0.5
                            }
                        }}
                    />
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Database Search Configuration */}
                <Box sx={{ mb: 3 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={config.enable_db_search}
                                onChange={(e) =>
                                    handleConfigChange(
                                        'enable_db_search',
                                        e.target.checked
                                    )
                                }
                                color="primary"
                            />
                        }
                        label={
                            <Box sx={{ ml: 1 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    <SearchIcon
                                        color="primary"
                                        fontSize="small"
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        Enable Database Search
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ mt: 0.5 }}
                                >
                                    Allow searching through connected databases
                                    in your knowledge base
                                </Typography>
                            </Box>
                        }
                        labelPlacement="end"
                        sx={{
                            alignItems: 'flex-start',
                            ml: 0,
                            '& .MuiFormControlLabel-label': {
                                mt: 0.5
                            }
                        }}
                    />
                </Box>

                {/* <Divider sx={{ my: 3 }} /> */}

                {/* Configuration Preview */}
                {/* <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Current Configuration
                    </Typography>
                    <Paper
                        sx={{
                            p: 2,
                            backgroundColor: '#f5f5f5',
                            border: '1px solid #e0e0e0'
                        }}
                    >
                        <Typography
                            variant="body2"
                            component="pre"
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                lineHeight: 1.5,
                                margin: 0
                            }}
                        >
                            {JSON.stringify(config, null, 2)}
                        </Typography>
                    </Paper>
                </Box> */}

                {/* Save Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveConfig}
                        disabled={isLoadingAiChatSettings || (!isInitialized && !aiChatSettings)}
                        sx={{
                            textTransform: 'none',
                            px: 3,
                            py: 1
                        }}
                    >
                        Save Configuration
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default Config;